import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Search, Filter, Ticket } from "lucide-react";
import EventCard, { priceFromCents } from "@/components/site/EventCard";
import { API_BASE } from "@/lib/supabase";

const PRICE_BUCKETS = [
  { key: "all", label: "Alle prijzen", max: null },
  { key: "free", label: "Gratis of onder € 5", max: 500 },
  { key: "u15", label: "Onder € 15", max: 1500 },
  { key: "u30", label: "Onder € 30", max: 3000 },
];

export default function Events() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [bucket, setBucket] = useState("all");

  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API_BASE}/events`, { params: { limit: 48 } });
        if (!ignore) setItems(data.items || []);
      } catch (e) {
        if (!ignore) setError(e?.response?.data?.detail || e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const activeBucket = PRICE_BUCKETS.find((b) => b.key === bucket);
    return items.filter((ev) => {
      if (q) {
        const hay = [ev.title, ev.location, ev.organization?.name].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (activeBucket?.max != null) {
        const min = priceFromCents(ev.ticket_types);
        if (min == null || min > activeBucket.max) return false;
      }
      return true;
    });
  }, [items, query, bucket]);

  return (
    <div data-testid="events-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
      <div className="flex flex-col gap-3 max-w-3xl">
        <div className="text-xs uppercase tracking-widest text-svj-cyan font-semibold">Alle events</div>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
          Kies je volgende{" "}
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple">
            avond uit
          </span>
        </h1>
        <p className="text-base text-slate-300 max-w-2xl">
          Festivals, buurtevents en avonden dichtbij. Uitverkochte events blijven zichtbaar zodat je weet wat je gemist hebt.
        </p>
      </div>

      {/* Filters */}
      <div className="mt-10 flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Zoek op titel, plaats of organisatie…"
            data-testid="events-search-input"
            className="w-full h-12 pl-11 pr-4 rounded-full bg-svj-card border border-svj-border text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-svj-cyan focus:border-transparent text-base"
          />
        </div>
        <div className="flex items-center gap-2 overflow-x-auto md:overflow-visible">
          <Filter className="h-4 w-4 text-slate-400 shrink-0" />
          {PRICE_BUCKETS.map((b) => (
            <button
              key={b.key}
              type="button"
              data-testid={`events-filter-${b.key}`}
              onClick={() => setBucket(b.key)}
              className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${
                bucket === b.key
                  ? "bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white border-transparent"
                  : "bg-svj-card border-svj-border text-slate-300 hover:text-white hover:border-svj-cyan/60"
              }`}
            >
              {b.label}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div className="mt-10">
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="animate-pulse bg-svj-card border border-svj-border rounded-2xl h-72" />
            ))}
          </div>
        )}
        {!loading && error && (
          <div data-testid="events-error" className="rounded-2xl border border-svj-border bg-svj-card p-8 text-slate-300">
            <div className="text-white font-semibold mb-2">Kon events niet ophalen</div>
            <p className="text-sm text-slate-400">{String(error)}</p>
          </div>
        )}
        {!loading && !error && filtered.length === 0 && (
          <div
            data-testid="events-empty"
            className="rounded-2xl border border-svj-border bg-svj-card p-10 text-center"
          >
            <Ticket className="h-8 w-8 text-svj-cyan mx-auto" />
            <div className="mt-3 text-white font-semibold">Geen events gevonden</div>
            <p className="mt-1 text-sm text-slate-400">
              Pas je zoekopdracht of filters aan om andere events te zien.
            </p>
          </div>
        )}
        {!loading && !error && filtered.length > 0 && (
          <div
            data-testid="events-grid"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8"
          >
            {filtered.map((ev) => (
              <EventCard key={ev.id} ev={ev} />
            ))}
          </div>
        )}
        {!loading && !error && (
          <div className="mt-6 text-xs text-slate-500" data-testid="events-count">
            {filtered.length} van {items.length} events getoond
          </div>
        )}
      </div>
    </div>
  );
}
